import { AbstractGameView } from "@lib/titan2-slingo/dist/game/view/abstract/display/AbstractGameView";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { Emitter, LinkedListContainer, behaviors } from "@pixi/particle-emitter";
import { BlurFilter, Container, Graphics, LINE_CAP } from "pixi.js";
import { GameEvent } from "../../../controller/event/GameEvent";
import { GameSoundEvent } from "../../../controller/event/GameSoundEvent";
/**
 * Draws the win line.
 */
export class GameWinLine extends AbstractGameView {
    create() {
        super.create();
        Logger.addObjectRef(this, "GameWinLine");
        this.laser = new WinLineLaser(this.uiProperties.whiteThickness, this.uiProperties.glowThickness, this.uiProperties.glowBlur, this.renderer.getResolution(), this.uiProperties.roundness, this.uiProperties.colours);
        this.addChild(this.laser);
        this.particles = new WinLineParticles(this.uiProperties.emitterProperties, this.uiProperties.roundness);
        this.addChild(this.particles);
    }
    postCreate() {
        super.postCreate();
        this.clear();
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(GameEvent.WIN_LINE_SUSPENSE, this.handleWinLineSuspense);
        this.registerGame(GameEvent.WIN_LINE_COMPLETE, this.handleWinLineComplete);
        this.registerGame(GameEvent.WIN_LINE_ANIMATION, this.handleWinLineAnimation);
    }
    handleRotate(event) {
        super.handleRotate(event);
        this.redraw();
    }
    async handleWinLineSuspense() {
        const column = this.model.getReelDisplayData().getStopIndex();
        await this.moveToColumn(column - 1);
    }
    async handleWinLineComplete() {
        if (this.model.getWinLine().getNumberOfSymbols() === this.model.getReelDisplayData().getNumberOfReels()) {
            const column = this.model.getReelDisplayData().getStopIndex() - 1;
            await this.moveToColumn(column);
        }
        await this.expandToBorder();
    }
    async handleWinLineAnimation() {
        await gsap.to(this, { alpha: 0, duration: 0.4 });
        this.clear();
    }
    clear() {
        this.alpha = 1;
        this.length = 0;
        this.expansion = 0;
        this.redraw();
    }
    async moveToColumn(column) {
        this.dispatchGame(GameSoundEvent.WIN_LINE_INCREASE);
        await gsap.to(this, {
            length: column + 1,
            duration: 0.4,
            ease: "power2.inOut",
            onUpdate: () => this.redraw(),
        });
    }
    async expandToBorder() {
        this.dispatchGame(GameSoundEvent.WIN_LINE_COMPLETE);
        await gsap.to(this, {
            expansion: 1,
            duration: 0.4,
            ease: "power2.inOut",
            onUpdate: () => this.redraw(),
        });
    }
    *resizables() {
        yield this.laser;
        yield this.particles;
    }
    *restylables() {
        yield this.laser;
    }
    redraw() {
        const column = Math.floor(this.length);
        const columnOffset = this.length - column;
        const left = this.uiProperties.distances[column - 1] ?? 0;
        const right = this.uiProperties.distances[column] ?? left;
        const width = left + (right - left) * columnOffset;
        const height = this.expansion * this.uiProperties.expansion;
        for (const resizable of this.resizables()) {
            resizable.resize(width, height);
        }
        for (const restylable of this.restylables()) {
            restylable.restyle(column, columnOffset);
        }
    }
}
/**
 * Draws the laser for the win line.
 */
class WinLineLaser extends Container {
    constructor(whiteThickness, glowThickness, glowBlur, glowResolution, roundness, colours) {
        super();
        this.whiteLineStyle = { width: whiteThickness, cap: LINE_CAP.ROUND, color: 0xffffff };
        this.glowLineStyle = { width: glowThickness, cap: LINE_CAP.ROUND, color: 0xffffff };
        this.roundness = roundness;
        this.glowLines = colours.map(colour => {
            const glowLine = new Graphics();
            glowLine.tint = colour;
            const glowBlurFilter = new BlurFilter(glowBlur);
            glowBlurFilter.resolution = glowResolution;
            glowLine.filters = [glowBlurFilter];
            return glowLine;
        });
        this.whiteLine = new Graphics();
        this.addChild(...this.glowLines, this.whiteLine);
    }
    resize(width, height) {
        // Clear the white line.
        this.whiteLine.clear().lineStyle(this.whiteLineStyle);
        // Clear the glow line.
        this.glowLines.forEach(glowLine => glowLine.clear().lineStyle(this.glowLineStyle));
        // Draw a rounded rectangle unless the height is zero, in which case just draw a straight line.
        const drawLine = height > 0
            ? (lineGraphics) => lineGraphics.drawRoundedRect(0, -height / 2, width, height, height / 2 * this.roundness)
            : (lineGraphics) => lineGraphics.lineTo(width, 0);
        drawLine(this.whiteLine);
        this.glowLines.forEach(drawLine);
    }
    restyle(column, columnOffset) {
        // Fade out the previous column.
        if (column > 0) {
            this.glowLines[column - 1].alpha = 1 - columnOffset;
            for (let i = column - 2; i >= 0; --i) {
                this.glowLines[i].alpha = 0;
            }
        }
        // Fade in the current column.
        if (column < this.glowLines.length) {
            this.glowLines[column].alpha = columnOffset;
            for (let i = column + 1; i < this.glowLines.length; ++i) {
                this.glowLines[i].alpha = 0;
            }
        }
    }
}
/**
 * Emits the particles for the win line.
 */
class WinLineParticles extends LinkedListContainer {
    constructor(config, roundness) {
        super();
        this.spawnShape = new WinLineParticlesSpawnShape(roundness);
        this.emitter = new Emitter(this, {
            ...config,
            behaviors: [
                ...config.behaviors,
                { type: "spawnShape", config: this.spawnShape },
            ],
        });
        this.lineFrequency = config.frequency * 2;
        this.borderFrequency = config.frequency;
    }
    resize(width, height) {
        this.emitter.emit = width > 0;
        this.emitter.frequency = height > 0 ? this.borderFrequency : this.lineFrequency;
        this.spawnShape.resize(width, height);
    }
}
/**
 * Configures the shape for the win line particles.
 */
class WinLineParticlesSpawnShape {
    constructor(roundness) {
        this.type = WinLineParticlesSpawnShape.type;
        this.data = this;
        this.halfWidth = 0;
        this.halfHeight = 0;
        this.cornerRadius = 0;
        this.roundness = roundness;
    }
    resize(width, height) {
        this.halfWidth = width / 2;
        this.halfHeight = height / 2;
        this.cornerRadius = Math.min(this.halfWidth, this.halfHeight) * this.roundness;
    }
    getRandPos(particle) {
        if (this.halfHeight === 0) {
            //The height is zero so just sample along the horizontal axis.
            const offset = Math.random() * this.halfWidth * 2;
            particle.position.set(offset, 0);
            return;
        }
        // Instead of dealing with a rounded rectangle, it's simpler to handle a half-side in 4 directions.
        const direction = Math.floor(Math.random() * 4);
        // Weight the axis based on the aspect ratio of the rectangle so that particles are more visually evenly distributed.
        const aspectRatio = this.halfWidth / this.halfHeight;
        const isHorizontal = Math.random() < aspectRatio / (aspectRatio + 1);
        // With 2 side lengths, there are 8 alignments (4 directions for each axis).
        const alignment = direction * 2 + (isHorizontal ? 0 : 1);
        const halfLength = isHorizontal ? this.halfWidth : this.halfHeight;
        // Sample randomly parallel to the axis.
        const parallel = Math.random() * halfLength;
        // Sample through an arc perpendicular to the axis based on how much it overlaps the corner.
        const cornerOverlap = Math.max(this.cornerRadius - parallel, 0);
        const perpendicular = this.cornerRadius - Math.sqrt(this.cornerRadius * this.cornerRadius - cornerOverlap * cornerOverlap);
        // Offset horizontally or vertically.
        const [xOffset, yOffset] = isHorizontal ? [parallel, perpendicular] : [perpendicular, parallel];
        // Align to the left or the right.
        const [xStart, xMult] = (alignment % 4) < 2 ? [0, 1] : [this.halfWidth * 2, -1];
        // Align to the top or the bottom.
        const [yStart, yMult] = alignment < 4 ? [-this.halfHeight, 1] : [this.halfHeight, -1];
        particle.position.set(xStart + xOffset * xMult, yStart + yOffset * yMult);
    }
}
WinLineParticlesSpawnShape.type = "winLine";
(() => {
    var _a;
    // Use a thin wrapper class to forward this config through the actual shape.
    behaviors.ShapeSpawnBehavior.registerShape((_a = class {
            constructor(config) {
                this.config = config;
            }
            getRandPos(particle) {
                this.config.getRandPos(particle);
            }
        },
        _a.type = WinLineParticlesSpawnShape.type,
        _a));
})();
//# sourceMappingURL=GameWinLine.js.map